import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import CustomerSuccessCarousel from "./components/CustomerSuccessCarousel";
import WhySolarValley from "./components/WhySolarValley";
import LiveImpactCounter from "./components/LiveImpactCounter";
import CalculatorPreview from "./components/CalculatorPreview";
import LogoSolarValley from "../../components/img/SolarValley.svg";
import {
  useConfig
} from "../../context/ConfigContext";

const home = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const {
    COMPANY_NAME,
    PHONE_NUMBER_FORM,
    CALL_PHONE_NUMBER,
    WHATSAPP_NUMBER_FORM,
    EMAIL_ADDRESS,
    PLANTAS_SOLARES
  } = config;

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("home.title", { company: COMPANY_NAME })}</title>
        <meta name="description" content="Ahorra en tu factura de luz con Solar Valley Guatemala. Expertos en paneles solares, energía solar, limpieza solar y termografía. Solicita tu asesoría gratuita." />
        <meta name="keywords" content="Paneles Solares Guatemala, Energía Solar, Limpieza Solar, Termografía Solar, Ahorro Energía, Solar Valley" />
        <link rel="canonical" href="https://www.solarvalleygt.com/" />
        <meta property="og:title" content={t("home.title", { company: COMPANY_NAME })} />
        <meta property="og:description" content="Ahorra en tu factura de luz con Solar Valley Guatemala. Expertos en sistemas fotovoltaicos y mantenimiento solar." />
        <meta property="og:image" content="/og-image.png" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main>
          {/* Hero Section with Interactive Map */}
          <HeroSection />

          {/* Customer Success Stories Carousel */}
          <CustomerSuccessCarousel />

          {/* Why Choose Solar Valley */}
          <WhySolarValley />

          {/* Live Impact Counter */}
          <LiveImpactCounter />

          {/* Calculator Preview */}
          <CalculatorPreview />
        </main>

        {/* Footer */}
        <footer className="bg-text-primary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="relative">
                    <img src={LogoSolarValley} alt="Logo" width="100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{COMPANY_NAME}</h3>
                    <p className="text-sm opacity-80">
                      {t("common.clean_energy_slogan")}
                    </p>
                  </div>
                </div>
                <p className="text-sm opacity-80 leading-relaxed mb-4">
                  {t("common.footer_description", { count: PLANTAS_SOLARES })}
                </p>
                <div className="flex space-x-4">
                  <a
                    href={`tel:${CALL_PHONE_NUMBER}`}
                    className="text-sm hover:text-primary transition-colors"
                  >
                    📞 {PHONE_NUMBER_FORM}
                  </a>
                  <a
                    href={`mailto:${EMAIL_ADDRESS}?subject=${encodeURIComponent(
                      "Solicitud de Asesoría Comercial - Proyecto Solar"
                    )}&body=${encodeURIComponent(
                      `Buenos días,

                      Estoy interesado en instalar paneles solares en mi hogar y requiero:

                      🔋 Servicios solicitados:
                      - Evaluación técnica gratuita
                      - Propuesta comercial personalizada
                      - Información sobre financiamiento
                      - Cronograma de instalación

                      📋 Datos del proyecto:
                      - Tipo: Residencial
                      - Ubicación: [A especificar]
                      - Consumo mensual: [A especificar] kWh

                      Por favor, asignar un asesor comercial para coordinar la evaluación.

                      Gracias por su atención.

                      Saludos cordiales.`
                    )}`}
                    className="text-sm hover:text-primary transition-colors"
                  >
                    📧 {EMAIL_ADDRESS}
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-4">{t("common.quick_links")}</h4>
                <ul className="space-y-2 text-sm opacity-80">
                  <li>
                    <Link
                      to="/calculadora"
                      className="hover:text-primary transition-colors"
                    >
                      {t("common.solar_calculator")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/proyectos"
                      className="hover:text-primary transition-colors"
                    >
                      {t("common.our_projects")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/servicios"
                      className="hover:text-primary transition-colors"
                    >
                      {t("common.services")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/nosotros"
                      className="hover:text-primary transition-colors"
                    >
                      {t("common.about_us")}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold mb-4">{t("common.contact_us")}</h4>
                <div className="space-y-2 text-sm opacity-80">
                  <p>📍 {t("common.location")}</p>
                  <p>📱 WhatsApp: {WHATSAPP_NUMBER_FORM}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-8 pt-8 text-center">
              <p className="text-sm opacity-80">
                © {new Date()?.getFullYear()} {COMPANY_NAME} Guatemala. {t("common.rights_reserved")}
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default home;
